import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errorMessage } from '../services/api';
import { completeHumanCheck, humanCheckSiteKey, subscribeHumanCheck } from '../services/human-check';
import { TurnstileWidget } from './Turnstile';

export function HumanCheckDialog() {
  const siteKey = useSyncExternalStore(subscribeHumanCheck, humanCheckSiteKey, () => null);
  if (!siteKey) return null;
  return <HumanCheckPanel siteKey={siteKey} />;
}

function HumanCheckPanel({ siteKey }: { siteKey: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const [resetKey, setResetKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const onToken = (token: string | null) => {
    if (!token) return;
    setChecking(true);
    setError(null);
    api('/human-check', { body: { turnstileToken: token } })
      .then(() => completeHumanCheck())
      .catch((reason: unknown) => {
        setError(errorMessage(reason));
        setResetKey((key) => key + 1);
      })
      .finally(() => setChecking(false));
  };

  return (
    <dialog
      ref={ref}
      className="human-check"
      aria-labelledby="human-check-title"
      aria-describedby="human-check-text"
      onCancel={(event) => event.preventDefault()}
    >
      <div className="human-check__panel">
        <h2 id="human-check-title" className="human-check__title">
          {t('humanCheck.title')}
        </h2>
        <p id="human-check-text" className="human-check__text">
          {t('humanCheck.body')}
        </p>
        <TurnstileWidget siteKey={siteKey} action="session_check" onToken={onToken} resetKey={resetKey} />
        <p className="human-check__status" role="status" aria-live="polite">
          {error ?? (checking ? t('humanCheck.checking') : '')}
        </p>
      </div>
    </dialog>
  );
}
